import { useEffect, useRef, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;

  /** Buttons for the bottom bar. Omitted for a read-only preview, which only needs its close button. */
  footer?: ReactNode;

  /** Wider layout for a detail preview, as opposed to a confirmation. */
  wide?: boolean;
}

/**
 * A dialog built on the native <c>&lt;dialog&gt;</c> element rather than a div overlay: focus
 * trapping, Escape, inertness of the page behind it and the top-layer stacking all come for free and
 * correct, where a hand-rolled overlay gets each of them subtly wrong.
 */
export function Modal({ open, title, onClose, children, footer, wide }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={wide ? 'modal modal--wide' : 'modal'}
      // Fired by Escape as well as by close(), so this is the one place that has to report it.
      onClose={onClose}
      // A click that lands on the dialog element itself is a click on the backdrop: its own content
      // sits inside .modal__body/.modal__header, which stop it from reaching here.
      onClick={(event) => {
        if (event.target === ref.current) {
          onClose();
        }
      }}
    >
      <div className="modal__header">
        <h2 className="modal__title">{title}</h2>
        <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="modal__body">{children}</div>

      {footer && <div className="modal__footer">{footer}</div>}
    </dialog>
  );
}
